import React, { useState, useEffect, useCallback } from "react";
import CopyableField from './CopyableField';
import OfferEdit from "./OfferEdit";
import axios from "axios";

const API = "/api";

interface OfferPromo {
  id: string;
  offer_id: string;
  offer_name: string;
  offer_image: string;
  offer_url: string;
  account_id: string;
  account_name: string;
  package_id: string;
  marketplace_id: string;
  price: number;
  currency: string;
  valid_from: string | null;
  valid_to: string | null;
  next_cycle_date: string | null;
  sold_during_promo: number;
  revenue_during_promo: number;
  roas: number | null;
  promo_cost: number;
  auto_disable_enabled: boolean;
  min_revenue: number | null;
  min_quantity: number | null;
  min_roas: number | null;
  disable_scheduled: boolean;
  is_active: boolean;
  sku: string;
  ean: string;
}

interface OfferPromosProps { accounts: any[]; }

function formatDate(s: string | null): string {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return s; }
}

function daysLeft(s: string | null): number | null {
  if (!s) return null;
  const diff = new Date(s).getTime() - Date.now();
  return Math.ceil(diff / 86400000);
}

type SortCol = "valid_to" | "sold" | "revenue" | "roas";
type SortDir = "asc" | "desc";

const emptyForm = { auto_disable_enabled: false, min_revenue: "", min_quantity: "", min_roas: "", promo_cost: "" };

export default function OfferPromos({ accounts }: OfferPromosProps) {
  const [promos, setPromos] = useState<OfferPromo[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [accountFilter, setAccountFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [sortCol, setSortCol] = useState<SortCol>("valid_to");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editPromo, setEditPromo] = useState<OfferPromo | null>(null);
  const [editOfferData, setEditOfferData] = useState<{id:string, accountId:string, accountName:string} | null>(null);
  const [bulkEdit, setBulkEdit] = useState(false);
  const [bulkTouched, setBulkTouched] = useState<{[k: string]: boolean}>({});
  const [editForm, setEditForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [disabling, setDisabling] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [autoDisableFilter, setAutoDisableFilter] = useState<"all" | "active" | "none">("all");
  const isMobile = window.innerWidth < 768;

  const s: any = {
    btn: { border: "none", borderRadius: 6, padding: "7px 14px", fontSize: 12, cursor: "pointer", fontWeight: 500 },
    input: { border: "1px solid #d1d5db", borderRadius: 6, padding: "7px 10px", fontSize: 13, outline: "none" },
    th: { cursor: "pointer", userSelect: "none" as const, display: "flex", alignItems: "center", gap: 3 },
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API + "/offers/promos", { params: { account_id: accountFilter, country: countryFilter } });
      setPromos(res.data.promos || []);
    } catch { setStatusMsg("Blad ladowania"); }
    setLoading(false);
  }, [accountFilter, countryFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSort = (col: SortCol) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };

  const SortIcon = ({ col }: { col: SortCol }) => {
    if (sortCol !== col) return <span style={{ color: "#d1d5db", fontSize: 10 }}>&#8597;</span>;
    return <span style={{ color: "#1d4ed8", fontSize: 10 }}>{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  const filtered = promos.filter(p => {
    if (autoDisableFilter === "active" && !p.auto_disable_enabled) return false;
    if (autoDisableFilter === "none" && p.auto_disable_enabled) return false;
    if (!search) return true;
    const q = search.trim().replace(/\s+/g, " ").toLowerCase();
    return p.offer_name.toLowerCase().includes(q) ||
      (p.sku && p.sku.toLowerCase().includes(q)) ||
      (p.ean && p.ean.toLowerCase().includes(q)) ||
      (p.offer_id && p.offer_id.toLowerCase().includes(q));
  });

  const sorted = [...filtered].sort((a, b) => {
    let va: number = 0, vb: number = 0;
    if (sortCol === "valid_to") {
      va = a.valid_to ? new Date(a.valid_to).getTime() : (a.next_cycle_date ? new Date(a.next_cycle_date).getTime() : 0);
      vb = b.valid_to ? new Date(b.valid_to).getTime() : (b.next_cycle_date ? new Date(b.next_cycle_date).getTime() : 0);
    } else if (sortCol === "sold") {
      va = a.sold_during_promo; vb = b.sold_during_promo;
    } else if (sortCol === "revenue") {
      va = a.revenue_during_promo; vb = b.revenue_during_promo;
    } else if (sortCol === "roas") {
      va = a.roas ?? -1; vb = b.roas ?? -1;
    }
    return sortDir === "asc" ? va - vb : vb - va;
  });

  const toggleSelect = (id: string) => {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const selectAll = () => {
    if (selected.size === sorted.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(p => p.id)));
  };

  const doSearch = () => { setSearch(searchInput); };

  const syncPromos = async () => {
    setSyncing(true); setStatusMsg("Synchronizuje wyroznienia...");
    try {
      const r = await axios.post(API + "/offers/promos/sync");
      setStatusMsg("Zsynchronizowano: " + r.data.total + " aktywnych wyroznie");
      load();
    } catch { setStatusMsg("Blad synchronizacji"); }
    setSyncing(false); setTimeout(() => setStatusMsg(""), 5000);
  };

  const openEdit = (promo: OfferPromo) => {
    setEditPromo(promo); setBulkEdit(false);
    setEditForm({
      auto_disable_enabled: promo.auto_disable_enabled,
      min_revenue: promo.min_revenue != null ? String(promo.min_revenue) : "",
      min_quantity: promo.min_quantity != null ? String(promo.min_quantity) : "",
      min_roas: promo.min_roas != null ? String(promo.min_roas) : "",
      promo_cost: promo.promo_cost ? String(promo.promo_cost) : "",
    });
  };

  const openBulkEdit = () => {
    setBulkEdit(true); setEditPromo(null);
    setEditForm({ ...emptyForm });
    setBulkTouched({});
  };

  const saveConditions = async () => {
    setSaving(true);
    try {
      if (bulkEdit) {
        const ids = Array.from(selected);
        const payload: any = { promo_ids: ids };
        if (bulkTouched.auto_disable_enabled) payload.auto_disable_enabled = editForm.auto_disable_enabled;
        if (bulkTouched.min_revenue) payload.min_revenue = editForm.min_revenue ? parseFloat(editForm.min_revenue) : null;
        if (bulkTouched.min_quantity) payload.min_quantity = editForm.min_quantity ? parseInt(editForm.min_quantity) : null;
        if (bulkTouched.min_roas) payload.min_roas = editForm.min_roas ? parseFloat(editForm.min_roas) : null;
        if (bulkTouched.promo_cost) payload.promo_cost = editForm.promo_cost ? parseFloat(editForm.promo_cost) : null;
        if (Object.keys(payload).length === 1) {
          setStatusMsg("Zaznacz przynajmniej jedno pole do zmiany");
          setSaving(false); setTimeout(() => setStatusMsg(""), 4000);
          return;
        }
        const res = await axios.put(API + "/offers/promos/bulk-conditions", payload);
        setStatusMsg("Warunki zapisane dla " + (res.data.updated ?? ids.length) + " wyroznie");
        setBulkEdit(false); setSelected(new Set()); setBulkTouched({});
      } else if (editPromo) {
        const payload = {
          auto_disable_enabled: editForm.auto_disable_enabled,
          min_revenue: editForm.min_revenue ? parseFloat(editForm.min_revenue) : null,
          min_quantity: editForm.min_quantity ? parseInt(editForm.min_quantity) : null,
          min_roas: editForm.min_roas ? parseFloat(editForm.min_roas) : null,
          promo_cost: editForm.promo_cost ? parseFloat(editForm.promo_cost) : null,
        };
        await axios.put(API + "/offers/promos/" + editPromo.id + "/conditions", payload);
        setStatusMsg("Warunki zapisane");
        setEditPromo(null);
      }
      load();
    } catch { setStatusMsg("Blad zapisu"); }
    setSaving(false); setTimeout(() => setStatusMsg(""), 4000);
  };

  const disablePromo = async (promoId: string, offerName: string) => {
    if (!window.confirm("Zaplanowac wylaczenie wyroznienia dla: " + offerName + "?")) return;
    setDisabling(promoId);
    try {
      await axios.post(API + "/offers/promos/" + promoId + "/disable");
      setStatusMsg("Wylaczenie zaplanowane na koniec okresu");
      load();
    } catch (e: any) {
      setStatusMsg("Blad: " + (e.response?.data?.detail || "nieznany"));
    }
    setDisabling(null); setTimeout(() => setStatusMsg(""), 5000);
  };

  const countryBadge = (mkt: string): [string, string, string, string] => {
    const m: {[k: string]: [string, string, string, string]} = { "allegro-pl": ["🇵🇱", "PL", "#eff6ff", "#1d4ed8"], "allegro-cz": ["🇨🇿", "CZ", "#f0fdf4", "#15803d"], "allegro-hu": ["🇭🇺", "HU", "#fef3c7", "#92400e"], "allegro-sk": ["🇸🇰", "SK", "#fef2f2", "#b91c1c"] };
    return m[mkt] || m["allegro-pl"];
  };
  const packageLabel = (pkg: string) => {
    if (pkg.includes("emphasized")) return "Wyroznienie";
    if (pkg.includes("promo")) return "Pakiet promo";
    if (pkg.includes("bold")) return "Pogrubienie";
    return pkg;
  };

  const showModal = editPromo !== null || bulkEdit;

  return (
    <>
    <div>
      {/* Toolbar + wyszukiwarka */}
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 10, marginBottom: 10 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" as const, alignItems: "center" }}>
          <div style={{ display: "flex", gap: 4, flex: isMobile ? "1 1 100%" : "1 1 280px" }}>
            <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") doSearch(); }}
              placeholder="Szukaj nazwy, SKU, EAN, nr oferty..." style={{ ...s.input, flex: 1 }} />
            <button onClick={doSearch} style={{ ...s.btn, background: "#1d4ed8", color: "#fff" }}>Szukaj</button>
          </div>
          <select value={accountFilter} onChange={e => setAccountFilter(e.target.value)} style={{ ...s.input, ...(isMobile ? { flex: "1 1 100%" } : {}) }}>
            <option value="">Wszystkie konta</option>
            {accounts.map((a: any) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <select value={countryFilter} onChange={e => setCountryFilter(e.target.value)} style={{ ...s.input, ...(isMobile ? { flex: "1 1 100%" } : {}) }}>
            <option value="">Wszystkie kraje</option>
            <option value="pl">🇵🇱 Polska</option>
            <option value="cz">🇨🇿 Czechy</option>
            <option value="hu">🇭🇺 Węgry</option>
            <option value="sk">🇸🇰 Słowacja</option>
          </select>
          <select value={autoDisableFilter} onChange={e => setAutoDisableFilter(e.target.value as "all" | "active" | "none")} style={{ ...s.input, ...(isMobile ? { flex: "1 1 100%" } : {}) }}>
            <option value="all">Wszystkie statusy auto-wylaczenia</option>
            <option value="active">Auto wylaczenie aktywne</option>
            <option value="none">Bez auto wylaczenia</option>
          </select>
          {search && (
            <button onClick={() => { setSearch(""); setSearchInput(""); }}
              style={{ ...s.btn, background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db" }}>x</button>
          )}
          <button onClick={syncPromos} disabled={syncing} style={{ ...s.btn, background: "#1d4ed8", color: "#fff", ...(isMobile ? { flex: "1 1 100%" } : { marginLeft: "auto" }) }}>
            {syncing ? "Synchronizuje..." : "Synchronizuj wyroznienia"}
          </button>
        </div>
      </div>

      {statusMsg && (
        <div style={{ fontSize: 12, color: "#15803d", fontWeight: 600, marginBottom: 10, padding: "6px 10px", background: "#f0fdf4", borderRadius: 6, border: "1px solid #bbf7d0" }}>
          {statusMsg}
        </div>
      )}

      {/* Bulk bar */}
      {selected.size > 0 && (
        <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "8px 12px", marginBottom: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" as const }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#92400e" }}>Zaznaczono: {selected.size}</span>
          <button onClick={openBulkEdit} style={{ ...s.btn, background: "#1d4ed8", color: "#fff" }}>Ustaw warunki ({selected.size})</button>
          <button onClick={() => setSelected(new Set())} style={{ ...s.btn, background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db" }}>Odznacz</button>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: "center" as const, color: "#9ca3af" }}>Ladowanie...</div>
      ) : promos.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center" as const, color: "#9ca3af" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>&#11088;</div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Brak aktywnych wyroznie</div>
          <div style={{ fontSize: 12 }}>Kliknij "Synchronizuj wyroznienia" aby pobrac dane z Allegro</div>
        </div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>

          {/* Nagłówek — tylko desktop */}
          {!isMobile && (
            <div style={{ display: "grid", gridTemplateColumns: "32px 56px 1fr 90px 90px 100px 90px 90px 80px 110px", gap: 8, padding: "10px 12px", background: "#f9fafb", borderBottom: "2px solid #e5e7eb", fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase" as const }}>
              <div><input type="checkbox" checked={selected.size === sorted.length && sorted.length > 0} onChange={selectAll} /></div>
              <div>Foto</div>
              <div>Oferta</div>
              <div>Cena</div>
              <div>Pakiet</div>
              <div style={s.th} onClick={() => handleSort("valid_to")}>Wazne do <SortIcon col="valid_to" /></div>
              <div style={s.th} onClick={() => handleSort("sold")}>Sprzedaz <SortIcon col="sold" /></div>
              <div style={s.th} onClick={() => handleSort("revenue")}>Przychod <SortIcon col="revenue" /></div>
              <div style={s.th} onClick={() => handleSort("roas")}>ROAS <SortIcon col="roas" /></div>
              <div>Akcje</div>
            </div>
          )}

          {/* Nagłówek mobile — checkbox + sortowanie */}
          {isMobile && (
            <div style={{ padding: "8px 12px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <input type="checkbox" checked={selected.size === sorted.length && sorted.length > 0} onChange={selectAll} />
                <span style={{ fontSize: 11, color: "#6b7280" }}>Zaznacz wszystkie</span>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" as const }}>
                {([["valid_to","Wazne do"],["sold","Sprzedaz"],["revenue","Przychod"],["roas","ROAS"]] as [SortCol,string][]).map(([col, label]) => (
                  <button key={col} onClick={() => handleSort(col)} style={{ ...s.btn, background: sortCol === col ? "#1d4ed8" : "#f3f4f6", color: sortCol === col ? "#fff" : "#374151", border: "1px solid #d1d5db", padding: "6px 14px", fontSize: 12, flex: "1 1 auto" }}>
                    {label} {sortCol === col ? (sortDir === "asc" ? "↑" : "↓") : ""}
                  </button>
                ))}
              </div>
            </div>
          )}

          {sorted.map(promo => {
            const days = daysLeft(promo.valid_to || promo.next_cycle_date);
            const daysColor = days !== null && days <= 2 ? "#dc2626" : days !== null && days <= 5 ? "#d97706" : "#15803d";

            if (isMobile) return (
              <div key={promo.id} style={{ padding: "10px 12px", borderBottom: "1px solid #f3f4f6", background: selected.has(promo.id) ? "#eff6ff" : promo.disable_scheduled ? "#fef2f2" : "#fff" }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <input type="checkbox" checked={selected.has(promo.id)} onChange={() => toggleSelect(promo.id)} style={{ marginTop: 4, flexShrink: 0 }} />
                  {promo.offer_image
                    ? <img src={promo.offer_image} alt="" style={{ width: 50, height: 50, objectFit: "cover" as const, borderRadius: 6, flexShrink: 0, border: "1px solid #e5e7eb" }} />
                    : <div style={{ width: 50, height: 50, background: "#f3f4f6", borderRadius: 6, flexShrink: 0 }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#111827", marginBottom: 2 }}>{promo.offer_name}</div>
                    <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 2 }}>
                      {promo.sku && <CopyableField label="SKU" value={promo.sku} />}<CopyableField label="nr" value={promo.offer_id} muted />
                    </div>
                    <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>{promo.account_name}</div>
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" as const, alignItems: "center", marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>{promo.price?.toFixed(2)} {promo.currency}</span>
                      <span style={{ fontSize: 11, background: "#ede9fe", color: "#7c3aed", borderRadius: 4, padding: "1px 6px", fontWeight: 600 }}>{packageLabel(promo.package_id)}</span>{(() => { const cb = countryBadge(promo.marketplace_id); return <span style={{ fontSize: 11, background: cb[2], color: cb[3], borderRadius: 4, padding: "1px 6px", fontWeight: 700, marginLeft: 4 }}>{cb[0]} {cb[1]}</span>; })()}
                      <span style={{ fontSize: 11, fontWeight: 600, color: daysColor }}>{!promo.valid_to && promo.next_cycle_date ? "nast. cykl: " : "do "}{formatDate(promo.valid_to || promo.next_cycle_date)}{days !== null ? " (" + (days > 0 ? days + "d" : "wygaslo") + ")" : ""}</span>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" as const, fontSize: 12, marginBottom: 4 }}>
                      <span><b>{promo.sold_during_promo}</b> szt.</span>
                      <span><b>{promo.revenue_during_promo?.toFixed(2)}</b> zl</span>
                      {promo.roas !== null && <span>ROAS: <b style={{ color: promo.roas >= (promo.min_roas || 0) ? "#15803d" : "#dc2626" }}>{promo.roas?.toFixed(1)}x</b></span>}
                    </div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" as const }}>
                      {promo.disable_scheduled && <span style={{ fontSize: 10, background: "#fef2f2", color: "#dc2626", borderRadius: 3, padding: "1px 5px", fontWeight: 600 }}>Wylaczenie zaplanowane</span>}
                      {promo.auto_disable_enabled && !promo.disable_scheduled && <span style={{ fontSize: 10, background: "#fef3c7", color: "#92400e", borderRadius: 3, padding: "1px 5px", fontWeight: 600 }}>Auto-wylaczenie</span>}
                    </div>
                    <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                      {promo.valid_to ? (
                        <span style={{ fontSize: 11, color: "#9ca3af", padding: "4px 10px", border: "1px solid #e5e7eb", borderRadius: 4 }}>Bez odnawiania</span>
                      ) : (
                        <button onClick={() => openEdit(promo)} style={{ ...s.btn, background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db", padding: "4px 10px", fontSize: 11 }}>Warunki</button>
                      )}
                      {!promo.disable_scheduled && (
                        <button onClick={() => disablePromo(promo.id, promo.offer_name)} disabled={disabling === promo.id}
                          style={{ ...s.btn, background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", padding: "4px 10px", fontSize: 11 }}>
                          {disabling === promo.id ? "..." : "Wylacz"}
                        </button>
                      )}
                      <a href={promo.offer_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "#1d4ed8", alignSelf: "center" }}>Allegro</a>
                      <button onClick={() => setEditOfferData({id: promo.offer_id, accountId: promo.account_id, accountName: promo.account_name})}
                        style={{ fontSize: 10, padding: "2px 6px", border: "1px solid #d1d5db", borderRadius: 4, background: "#fff", cursor: "pointer" }}>✏️ Edytuj</button>
                    </div>
                  </div>
                </div>
              </div>
            );

            return (
              <div key={promo.id} style={{ display: "grid", gridTemplateColumns: "32px 56px 1fr 90px 90px 100px 90px 90px 80px 110px", gap: 8, padding: "10px 12px", borderBottom: "1px solid #f3f4f6", alignItems: "center", fontSize: 12, background: selected.has(promo.id) ? "#eff6ff" : promo.disable_scheduled ? "#fef2f2" : "#fff" }}>
                <div><input type="checkbox" checked={selected.has(promo.id)} onChange={() => toggleSelect(promo.id)} /></div>
                <div>
                  {promo.offer_image
                    ? <img src={promo.offer_image} alt="" style={{ width: 46, height: 46, objectFit: "cover" as const, borderRadius: 4, border: "1px solid #e5e7eb" }} />
                    : <div style={{ width: 46, height: 46, background: "#f3f4f6", borderRadius: 4 }} />}
                </div>
                <div>
                  <div style={{ fontWeight: 500, color: "#111827", marginBottom: 2, lineHeight: 1.3 }}>{promo.offer_name}</div>
                  <div style={{ fontSize: 11, color: "#6b7280" }}>
                    {promo.sku && <CopyableField label="SKU" value={promo.sku} />}<CopyableField label="nr" value={promo.offer_id} muted />
                  </div>
                  <div style={{ fontSize: 11, color: "#6b7280" }}>{promo.account_name}</div>
                  <div style={{ display: "flex", gap: 4, marginTop: 2, flexWrap: "wrap" as const }}>
                    <a href={promo.offer_url} target="_blank" rel="noreferrer" style={{ fontSize: 10, color: "#1d4ed8" }}>Allegro</a>
                    {promo.disable_scheduled && <span style={{ fontSize: 10, background: "#fef2f2", color: "#dc2626", borderRadius: 3, padding: "1px 5px", fontWeight: 600 }}>Wylaczenie zaplanowane</span>}
                    {promo.auto_disable_enabled && !promo.disable_scheduled && <span style={{ fontSize: 10, background: "#fef3c7", color: "#92400e", borderRadius: 3, padding: "1px 5px", fontWeight: 600 }}>Auto-wylaczenie aktywne</span>}
                  </div>
                </div>
                <div style={{ fontWeight: 600 }}>{promo.price?.toFixed(2)} {promo.currency}</div>
                <div><span style={{ fontSize: 11, background: "#ede9fe", color: "#7c3aed", borderRadius: 4, padding: "2px 6px", fontWeight: 600 }}>{packageLabel(promo.package_id)}</span>{(() => { const cb = countryBadge(promo.marketplace_id); return <span style={{ fontSize: 11, background: cb[2], color: cb[3], borderRadius: 4, padding: "1px 6px", fontWeight: 700, marginLeft: 4 }}>{cb[0]} {cb[1]}</span>; })()}</div>
                <div>
                  <div style={{ fontWeight: 600, color: daysColor }}>{formatDate(promo.valid_to || promo.next_cycle_date)}</div>
                  {days !== null && <div style={{ fontSize: 10, color: daysColor }}>{days > 0 ? days + " dni" : "Wygaslo"}</div>}
                  {!promo.valid_to && promo.next_cycle_date && <div style={{ fontSize: 10, color: "#9ca3af" }}>nast. cykl</div>}
                </div>
                <div>
                  <div style={{ fontWeight: 600 }}>{promo.sold_during_promo} szt.</div>
                  <div style={{ fontSize: 10, color: "#6b7280" }}>od startu</div>
                </div>
                <div>
                  <div style={{ fontWeight: 600 }}>{promo.revenue_during_promo?.toFixed(2)} zl</div>
                  {promo.promo_cost > 0 && <div style={{ fontSize: 10, color: "#6b7280" }}>koszt: {promo.promo_cost?.toFixed(2)} zl</div>}
                </div>
                <div>
                  {promo.roas !== null
                    ? <span style={{ fontWeight: 600, color: promo.roas >= (promo.min_roas || 0) ? "#15803d" : "#dc2626" }}>{promo.roas?.toFixed(1)}x</span>
                    : <span style={{ color: "#9ca3af" }}>-</span>}
                </div>
                <div style={{ display: "flex", flexDirection: "column" as const, gap: 4 }}>
                  {promo.valid_to ? (
                    <span style={{ fontSize: 11, color: "#9ca3af", padding: "4px 8px", border: "1px solid #e5e7eb", borderRadius: 4, textAlign: "center" as const }}>Bez odnawiania</span>
                  ) : (
                    <button onClick={() => openEdit(promo)} style={{ ...s.btn, background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db", padding: "4px 8px", fontSize: 11 }}>Warunki</button>
                  )}
                  {!promo.disable_scheduled && (
                    <button onClick={() => disablePromo(promo.id, promo.offer_name)} disabled={disabling === promo.id}
                      style={{ ...s.btn, background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", padding: "4px 8px", fontSize: 11 }}>
                      {disabling === promo.id ? "..." : "Wylacz"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal warunków */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 24, width: isMobile ? "92vw" : 460, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", maxHeight: "90vh", overflowY: "auto" as const }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Warunki auto-wylaczenia</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 16 }}>
              {bulkEdit ? "Ustawiasz warunki dla " + selected.size + " wyroznie" : editPromo?.offer_name}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: bulkEdit ? 4 : 20, padding: "10px 12px", background: editForm.auto_disable_enabled ? "#f0fdf4" : "#f9fafb", borderRadius: 8, border: "1px solid " + (editForm.auto_disable_enabled ? "#bbf7d0" : "#e5e7eb") }}>
              <input type="checkbox" id="auto_disable" checked={editForm.auto_disable_enabled}
                onChange={e => { setEditForm(f => ({ ...f, auto_disable_enabled: e.target.checked })); if (bulkEdit) setBulkTouched(t => ({ ...t, auto_disable_enabled: true })); }}
                style={{ width: 16, height: 16, cursor: "pointer" }} />
              <label htmlFor="auto_disable" style={{ fontSize: 13, fontWeight: 600, cursor: "pointer", color: editForm.auto_disable_enabled ? "#15803d" : "#374151" }}>
                Wlacz automatyczne wylaczenie wyroznienia
              </label>
            </div>
            {bulkEdit && (
              <label style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16, fontSize: 11, color: "#6b7280", cursor: "pointer" }}>
                <input type="checkbox" checked={!!bulkTouched.auto_disable_enabled}
                  onChange={e => setBulkTouched(t => ({ ...t, auto_disable_enabled: e.target.checked }))}
                  style={{ width: 13, height: 13, cursor: "pointer" }} />
                Zmien ten przelacznik dla wszystkich zaznaczonych
              </label>
            )}

            {(editForm.auto_disable_enabled || bulkEdit) && (
              <>
                <div style={{ marginBottom: 16, padding: "12px 14px", background: "#fafafa", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>Koszt wyroznienia (PLN)</div>
                    {bulkEdit && (
                      <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#6b7280", cursor: "pointer" }}>
                        <input type="checkbox" checked={!!bulkTouched.promo_cost}
                          onChange={e => setBulkTouched(t => ({ ...t, promo_cost: e.target.checked }))}
                          style={{ width: 13, height: 13, cursor: "pointer" }} /> Zmien
                      </label>
                    )}
                  </div>
                  <input type="number" step="0.01" min="0" value={editForm.promo_cost} disabled={bulkEdit && !bulkTouched.promo_cost}
                    onChange={e => setEditForm(f => ({ ...f, promo_cost: e.target.value }))}
                    placeholder="np. 2.50" style={{ ...s.input, width: "100%", boxSizing: "border-box" as const, opacity: (bulkEdit && !bulkTouched.promo_cost) ? 0.5 : 1 }} />
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>Wymagane do obliczenia ROAS</div>
                </div>

                <div style={{ marginBottom: 16, padding: "12px 14px", background: "#fafafa", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>
                      Warunek 1 - Minimalna sprzedaz
                      <span style={{ fontWeight: 400, color: "#9ca3af", marginLeft: 6 }}>(oba musza byc spelnione)</span>
                    </div>
                    {bulkEdit && (
                      <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#6b7280", cursor: "pointer", flexShrink: 0 }}>
                        <input type="checkbox" checked={!!bulkTouched.min_revenue && !!bulkTouched.min_quantity}
                          onChange={e => setBulkTouched(t => ({ ...t, min_revenue: e.target.checked, min_quantity: e.target.checked }))}
                          style={{ width: 13, height: 13, cursor: "pointer" }} /> Zmien
                      </label>
                    )}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div>
                      <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>Min. przychod (PLN)</label>
                      <input type="number" step="0.01" min="0" value={editForm.min_revenue} disabled={bulkEdit && !bulkTouched.min_revenue}
                        onChange={e => setEditForm(f => ({ ...f, min_revenue: e.target.value }))}
                        placeholder="np. 200" style={{ ...s.input, width: "100%", boxSizing: "border-box" as const, opacity: (bulkEdit && !bulkTouched.min_revenue) ? 0.5 : 1 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>Min. ilosc (szt.)</label>
                      <input type="number" min="0" value={editForm.min_quantity} disabled={bulkEdit && !bulkTouched.min_quantity}
                        onChange={e => setEditForm(f => ({ ...f, min_quantity: e.target.value }))}
                        placeholder="np. 2" style={{ ...s.input, width: "100%", boxSizing: "border-box" as const, opacity: (bulkEdit && !bulkTouched.min_quantity) ? 0.5 : 1 }} />
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>Jesli przychod LUB ilosc ponizej minimum - wylacz</div>
                </div>

                <div style={{ marginBottom: 16, padding: "12px 14px", background: "#fafafa", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>Warunek 2 - Minimalny ROAS</div>
                    {bulkEdit && (
                      <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#6b7280", cursor: "pointer" }}>
                        <input type="checkbox" checked={!!bulkTouched.min_roas}
                          onChange={e => setBulkTouched(t => ({ ...t, min_roas: e.target.checked }))}
                          style={{ width: 13, height: 13, cursor: "pointer" }} /> Zmien
                      </label>
                    )}
                  </div>
                  <input type="number" step="0.1" min="0" value={editForm.min_roas} disabled={bulkEdit && !bulkTouched.min_roas}
                    onChange={e => setEditForm(f => ({ ...f, min_roas: e.target.value }))}
                    placeholder="np. 3.0" style={{ ...s.input, width: "100%", boxSizing: "border-box" as const, opacity: (bulkEdit && !bulkTouched.min_roas) ? 0.5 : 1 }} />
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>ROAS = przychod / koszt wyroznienia. Jesli ponizej minimum - wylacz</div>
                </div>

                <div style={{ fontSize: 11, color: "#6b7280", background: "#fef3c7", borderRadius: 6, padding: "8px 10px", marginBottom: 16 }}>
                  Warunki sprawdzane automatycznie 24h przed koncem okresu wyroznienia
                </div>
              </>
            )}

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={saveConditions} disabled={saving} style={{ ...s.btn, background: "#1d4ed8", color: "#fff", flex: 1, padding: "10px" }}>
                {saving ? "Zapisuje..." : bulkEdit ? "Zapisz dla " + selected.size + " wyroznie" : "Zapisz warunki"}
              </button>
              <button onClick={() => { setEditPromo(null); setBulkEdit(false); }} style={{ ...s.btn, background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db" }}>
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    {editOfferData && <OfferEdit offerId={editOfferData.id} accountId={editOfferData.accountId} accountName={editOfferData.accountName}
      onClose={() => setEditOfferData(null)} onSaved={() => setEditOfferData(null)} />}
    </>
  );
}